// Assets

import Card from 'components/card';
import SearchSelector from 'components/fields/SearchSelector';
import { GoDotFill } from 'react-icons/go';
import CreateCategory from './CreateCategory';
import SubCategoryManager from './SubCategoryManager';
import CategoryManager from './CategoryManager';
import { useDisclosure } from '@chakra-ui/hooks';
import { useEffect, useState } from 'react';
import { getMyInfo, updateMyInfo } from 'interfaces/user';

const ProductCommandSettings = (props: { product: any; handleChange: any }) => {
    const { product, handleChange } = props;
    const defaultCategories = [
        {
            name: 'Produit vendable (sans catégorie)',
            id: 'all',
            _id: 'all',
        },
        {
            name: 'Produit non vendable',
            id: 'hidden',
            _id: 'hidden',
        },
    ];

    const { isOpen, onOpen, onClose } = useDisclosure();
    const {
        isOpen: isOpenSubCategoryManager,
        onOpen: onOpenSubCategoryManager,
        onClose: onCloseSubCategoryManager,
    } = useDisclosure();
    const {
        isOpen: isOpenCategoryManager,
        onOpen: onOpenCategoryManager,
        onClose: onCloseCategoryManager,
    } = useDisclosure();
    const [user, setUser] = useState(null);
    const [userSubCategories, setUserSubCategories] = useState<any[]>([]);

    const initUser = async () => {
        const res = await getMyInfo();
        if (res && res.data) {
            setUser(res.data);
            setUserSubCategories(res.data.subCategories || []);
        }
    };

    const saveSubCategories = async (subCategories: any[]) => {
        setUserSubCategories(subCategories);
        await updateMyInfo({ subCategories });
    };

    const saveCategories = async (categories: any[]) => {
        const res = await updateMyInfo({ categories });
        if (res) {
            setUser({
                ...user,
                categories,
            });
        }
    };

    const updateCategories = async (newCategory: any) => {
        console.log(newCategory);
        const newCategoryId = newCategory.name.toLowerCase().replace(' ', '-');
        if (user.categories.find((c: any) => c.id === newCategoryId)) return;

        const res = await updateMyInfo({
            categories: [...user.categories, newCategory],
        });
        if (res) {
            setUser({
                ...user,
                categories: [...user.categories, newCategory],
            });
        }
    };

    const sortAlpha = (array: any) => {
        if (!array) return [];

        return array.sort((a: any, b: any) => a.id.localeCompare(b.id));
    };

    useEffect(() => {
        initUser();
    }, []);

    return (
        <Card extra="px-5 py-5 border">
            <CreateCategory
                isOpen={isOpen}
                onClose={onClose}
                onClick={(newCategory: any) => updateCategories(newCategory)}
                existingCategories={user?.categories || []}
            />
            <div className="flex justify-between">
                <h4 className="text-lg font-medium">Boutique B2B</h4>
                {product?.category === 'hidden' && (
                    <div className="flex gap-1">
                        <p className="text-xs">Produit non vendable</p>
                        <GoDotFill className="text-red-600" />
                    </div>
                )}
                {product?.price > 0 && product.category !== 'hidden' && (
                    <div className="flex gap-[3px]">
                        <p className="text-xs">Produit visible</p>
                        <GoDotFill className="text-green-600" />
                    </div>
                )}
                {product?.price <= 0 && product.category !== 'hidden' && (
                    <div className="flex gap-1">
                        <p className="text-xs">
                            Visible pour les clients avec un prix défini
                        </p>
                        <GoDotFill className="text-toola-500" />
                    </div>
                )}
            </div>
            <div>
                <div className="mt-3 col-span-2">
                    <label className="text-navy-700 text-xs font-medium mb-2 block">
                        Catégorie
                    </label>
                    <div className="flex gap-2">
                        <SearchSelector
                            displayKey="name"
                            options={
                                user?.categories
                                    ? [
                                          ...defaultCategories,
                                          ...sortAlpha(user?.categories),
                                      ]
                                    : defaultCategories
                            }
                            value={product?.category}
                            onSelect={(e: any) => {
                                const categoryId = e?.id || e?._id;
                                console.log('Category selected:', categoryId, e);
                                handleChange('category', categoryId);
                            }}
                            defaultKey="id"
                            canCreate={true}
                            createNew={onOpen}
                            inputSmall={true}
                        />
                        <button
                            onClick={onOpenCategoryManager}
                            className="px-3 py-2 bg-toola-500 text-white rounded-xl text-sm hover:bg-toola-600 whitespace-nowrap"
                        >
                            Gérer
                        </button>
                    </div>
                </div>
                <div className="mt-5 col-span-3">
                    <label
                        htmlFor={'description'}
                        className={`text-navy-700 text-xs font-medium`}
                    >
                        Description
                    </label>
                    <textarea
                        id="textInput"
                        className="mt-1 w-full h-[35px] px-4 py-2 border rounded-xl text-sm my-auto focus:outline-none focus:ring focus:border-blue-300 transition-colors duration-300"
                        placeholder="Ajouter ma description ici..."
                        value={product?.description}
                        onChange={(e: any) => {
                            handleChange('description', e.target.value);
                        }}
                    ></textarea>
                </div>
                <div className="mt-5 col-span-3">
                    <label className="text-navy-700 text-xs font-medium mb-2 block">
                        Sous-catégorie du produit
                    </label>
                    <div className="flex gap-2">
                        <select
                            value={product?.productSubCategory || ''}
                            onChange={(e) => handleChange('productSubCategory', e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Aucune sous-catégorie</option>
                            {userSubCategories
                                .filter((subCat: any) => {
                                    // If no product, show all sub-categories
                                    if (!product?.category || product.category === 'all' || product.category === 'hidden') {
                                        return true;
                                    }
                                    // Or filter by categoryId
                                    return subCat.categoryId === product.category;
                                })
                                .map((subCat: any) => (
                                    <option key={subCat.id} value={subCat.name}>
                                        {subCat.name}
                                    </option>
                                ))}
                        </select>
                        <button
                            onClick={onOpenSubCategoryManager}
                            className="px-3 py-2 bg-toola-500 text-white rounded-xl text-sm hover:bg-toola-600 whitespace-nowrap"
                        >
                            Gérer
                        </button>
                    </div>
                </div>
                <SubCategoryManager
                    isOpen={isOpenSubCategoryManager}
                    onClose={onCloseSubCategoryManager}
                    categories={user?.categories || []}
                    subCategories={userSubCategories}
                    onSubCategoriesChange={saveSubCategories}
                />
                <CategoryManager
                    isOpen={isOpenCategoryManager}
                    onClose={onCloseCategoryManager}
                    categories={user?.categories || []}
                    onCategoriesChange={saveCategories}
                />
            </div>
        </Card>
    );
};

export default ProductCommandSettings;
